import { consumer, core, toJSON } from '@tock/coretypes';

import { assign, forEach, map } from 'es-toolkit/compat';

import AppConstants from 'constants/AppConstants';

export interface PurchaseState {
  calculatedPackagePrice?: core.PackagePurchaseOrder;
  calculatedPrice?: core.PurchaseOrder;
  checkoutPageData?: consumer.CheckoutDetails;
  keyValues: {
    [key: string]: string;
  };
  paymentError?: string;
  purchasedOrder?: core.PurchaseOrder;
  purchasedPackageOrder?: core.PackagePurchaseOrder;
  requestInProgress: boolean;
  ticketTypeDetails?: core.TicketTypeDetails;
  ticketTypeDiscountCode?: core.TicketTypePromoCode;
  /**
   * Net amount paid by the patron that initiated a transfer.
   * Note: this is stored separately because it is zeroed out on the purchase order
   * when we calculate the price of a transfer.
   * https://github.com/tocktix/admin/blob/master/server/src/main/java/com/tocktix/payments/PurchasePriceCalculator.java#L248
   */
  preTransferNetAmountCents?: number;
}

const initialState: PurchaseState = {
  calculatedPackagePrice: undefined,
  calculatedPrice: undefined,
  checkoutPageData: undefined,
  keyValues: {},
  paymentError: undefined,
  purchasedOrder: undefined,
  purchasedPackageOrder: undefined,
  requestInProgress: false,
  ticketTypeDetails: undefined,
  ticketTypeDiscountCode: undefined,
  preTransferNetAmountCents: undefined,
};

export default function reducer(
  state: PurchaseState = initialState,
  action: BaseTockAction
): PurchaseState {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.CALCULATE_PRICE_DONE: {
      const calculatedPrice = toJSON<core.PurchaseOrder>(payload);
      calculatedPrice.keyValue = map(state.keyValues, (value, key) => ({
        attribute: key,
        attributeValue: value,
      }));
      // context is a purchase
      if (calculatedPrice.totalPrice < (context as core.PastPurchase)?.amountPaidCents) {
        calculatedPrice.refundOnDowngrade = true;
      }
      return {
        ...state,
        calculatedPrice,
        calculatedPackagePrice: undefined,
        requestInProgress: false,
      };
    }
    case AppConstants.CALCULATE_PACKAGE_PRICE_DONE: {
      const calculatedPackagePrice = toJSON<core.PackagePurchaseOrder>(payload);
      const keyValue = map(state.keyValues, (value, key) => ({
        attribute: key,
        attributeValue: value,
      }));
      forEach(calculatedPackagePrice.purchaseOrder, (purchaseOrder) => {
        purchaseOrder.keyValue = keyValue;
      });
      return {
        ...state,
        calculatedPackagePrice,
        calculatedPrice: undefined,
        requestInProgress: false,
      };
    }
    case 'TRANSFER_PURCHASE_ORDER_FETCH_DONE': {
      const purchaseOrder = toJSON<core.PurchaseOrder>(payload);
      return {
        ...state,
        preTransferNetAmountCents: purchaseOrder.netAmountPaidCents,
      };
    }
    case AppConstants.DISCOUNT_CODE_VERIFY_DONE: {
      return {
        ...state,
        ticketTypeDiscountCode: toJSON(payload),
      };
    }
    case AppConstants.PAST_PURCHASE_CLEAR:
    case AppConstants.DISCOUNT_CODE_CLEAR: {
      return {
        ...state,
        ticketTypeDiscountCode: undefined,
      };
    }
    case AppConstants.PURCHASE_DONE: {
      return {
        ...state,
        purchasedOrder: toJSON(payload),
        calculatedPrice: undefined,
        requestInProgress: false,
        keyValues: {},
      };
    }
    case AppConstants.PURCHASE_PACKAGE_DONE: {
      return {
        ...state,
        purchasedPackageOrder: toJSON(payload),
        calculatedPackagePrice: undefined,
        requestInProgress: false,
        keyValues: {},
      };
    }
    case AppConstants.LOCK_TICKETS_DONE: {
      return {
        ...state,
        calculatedPrice: undefined,
        ticketTypeDiscountCode: undefined, // TK-4475: Clear applied discount codes on lock
      };
    }
    case AppConstants.PURCHASE_SET_KEY_VALUES: {
      return {
        ...state,
        keyValues: {
          ...state.keyValues,
          ...payload,
        },
      };
    }
    case AppConstants.PURCHASE_CLEAR: {
      return {
        ...initialState,
      };
    }
    case AppConstants.UPDATE_DINER: {
      return {
        ...state,
        calculatedPrice: assign(state.calculatedPrice, {
          dinerPatron: (payload ? [payload] : []) as core.Patron[],
        }),
      };
    }
    case AppConstants.CONCIERGE_BOOK_VIA_EMAIL_STARTING:
    case AppConstants.CANCEL_PURCHASE_STARTING:
    case AppConstants.PURCHASE_STARTING:
    case AppConstants.PURCHASE_PACKAGE_STARTING:
    case AppConstants.CALCULATE_PACKAGE_PRICE_STARTING:
    case AppConstants.CALCULATE_PRICE_STARTING:
    case AppConstants.CHECKOUT_DETAILS_FETCH_STARTING:
      return {
        ...state,
        paymentError: undefined,
        requestInProgress: true,
      };

    case AppConstants.CONCIERGE_BOOK_VIA_EMAIL_DONE:
    case AppConstants.CONCIERGE_BOOK_VIA_EMAIL_FAILED:
    case AppConstants.CANCEL_PURCHASE_DONE:
    case AppConstants.CANCEL_PURCHASE_FAILED:
    case AppConstants.PURCHASE_FAILED:
    case AppConstants.PURCHASE_PACKAGE_FAILED:
    case AppConstants.CALCULATE_PACKAGE_PRICE_FAILED:
    case AppConstants.CALCULATE_PRICE_FAILED:
    case AppConstants.CHECKOUT_DETAILS_FETCH_FAILED:
    case AppConstants.TICKET_TYPE_COMMUNICATIONS_FETCH_FAILED:
      return {
        ...state,
        requestInProgress: false,
      };
    case AppConstants.CHECKOUT_DETAILS_FETCH_DONE:
      return {
        ...state,
        checkoutPageData: payload,
        requestInProgress: false,
      };
    case AppConstants.PAYMENT_ERROR:
      return {
        ...state,
        paymentError: payload,
      };
    case AppConstants.TICKET_TYPE_COMMUNICATIONS_FETCH_STARTING:
      return {
        ...state,
        requestInProgress: true,
      };
    case AppConstants.TICKET_TYPE_COMMUNICATIONS_FETCH_DONE:
      return {
        ...state,
        ticketTypeDetails: payload,
        requestInProgress: false,
      };
    default:
      return state;
  }
}
